import userAtom from "@/atoms/userAtom";
import axios from "@/services/axios.config";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { toastOnError } from "@/utils/toastOnError";
import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

const getMethodologyRoundDetails = async (methodologyId: string, roundId?: string) =>
	axios
		.get<APIResponse<MethodologyWithRoundDetails>>("methodology/detail-round", {
			params: !!roundId ? { methodologyId, methodologyRoundId: roundId } : { methodologyId },
		})
		.then((res) => res.data.data)
		.catch((err) => Promise.reject(err.response.data as APIResponse));

export default function useMethodologyRoundDetails() {
	const user = useAtomValue(userAtom);
	const router = useRouter();

	const role = user?.roles[0];
	const methodologyId = router.query?.id as string | undefined;
	const roundId = router.query.roundId as string | undefined;

	const {
		data: roundDetails,
		isLoading: isLoadingRoundDetails,
		isError: isErrorLoadingRoundDetails,
		...rest
	} = useQuery({
		queryKey: ["methodologyDetailsWithRound", methodologyId, roundId],
		queryFn: () => getMethodologyRoundDetails(methodologyId!, roundId!),
		enabled: !!(methodologyId && roundId),
		retry: 0,
		onError: (err: APIResponse) => {
			if (![400, 403, 404].includes(err.httpStatus)) {
				toastOnError(err);
			}
		},
	});

	const sortedRounds = useMemo(() => {
		const rounds = roundDetails?.rounds || [];
		return rounds.sort((a, b) => a.roundNumber - b.roundNumber);
	}, [roundDetails?.rounds]);

	const selectedRound = useMemo(
		() => sortedRounds.find((round) => round.methodologyRoundId === Number(roundId)),
		[roundId, sortedRounds],
	);

	const setRoundId = useCallback(
		(roundId: string) => {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...router.query, roundId: roundId },
				},
				undefined,
				{ shallow: true },
			);
		},
		[router],
	);

	return {
		roundDetails,
		isLoadingRoundDetails,
		isErrorLoadingRoundDetails,
		role,
		sortedRounds,
		methodologyId,
		roundId,
		selectedRound,
		setRoundId,
		router,
		...rest,
	};
}

export function useBasicMethodologyRoundDetails({
	methodologyId,
	roundId,
}: {
	methodologyId?: string;
	roundId?: string;
}) {
	return useQuery({
		queryKey: ["basicMethodologyRoundDetails", methodologyId, roundId],
		queryFn: () => getMethodologyRoundDetails(methodologyId!, roundId),
		enabled: !!methodologyId,
		retry: 0,
		onError: (err: APIResponse) => {
			if (![400, 403, 404].includes(err.httpStatus)) {
				toastOnError(err);
			}
		},
	});
}
